// Mar 2, 2026 — CRITICAL: this MUST be the very first import so that
// `window.__WR_API_BASE__` is defined before any downstream module reads
// it at module-load time (see /src/setup-api-base.js for full rationale).
import "@/setup-api-base";
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "@/index.css";
import App from "@/App";

// Mar 2, 2026 — window.__WR_API_BASE__ is set in `/src/setup-api-base.js`
// which is imported at the top of this file BEFORE any other module so
// downstream modules see it at their module-load time. Previously the
// assignment lived here AFTER `import App`, causing a race where deep
// component modules loaded first and captured `undefined/api` — which
// manifested as 404s on /api/templates, /api/momentum-stats, and blank
// generation output. Don't move this back inline.

// Initialize Sentry for error monitoring
Sentry.init({
  dsn: "https://b904562929101d007146fb584afff200@o4511070366793728.ingest.us.sentry.io/4511070481809408",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Mask all text content to protect PHI/PII
      maskAllText: true,
      // Block all media to protect PHI/PII
      blockAllMedia: true,
    }),
  ],
  // Performance monitoring sample rate
  tracesSampleRate: 0.1, // 10% of transactions for performance
  // Session replay sample rate
  replaysSessionSampleRate: 0.0, // Disabled for PHI/PII protection
  replaysOnErrorSampleRate: 0.1, // 10% of error sessions get replayed
  
  // Environment configuration
  environment: process.env.NODE_ENV || "development",
  
  // Before sending to Sentry, strip any potential PHI/PII
  beforeSend(event, hint) {
    // Strip any user-identifiable information from breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
        // Remove any data that might contain PHI
        if (breadcrumb.data) {
          const cleanData = { ...breadcrumb.data };
          // Remove common PHI fields
          delete cleanData.notes;
          delete cleanData.clinicalNote;
          delete cleanData.chiefComplaint;
          delete cleanData.patientName;
          delete cleanData.dob;
          delete cleanData.firstName;
          delete cleanData.lastName;
          delete cleanData.email;
          delete cleanData.unit;
          delete cleanData.rank;
          breadcrumb.data = cleanData;
        }
        return breadcrumb;
      });
    }
    
    // Remove any extra context that might have PHI
    if (event.extra) {
      const cleanExtra = { ...event.extra };
      delete cleanExtra.notes;
      delete cleanExtra.clinicalNote;
      delete cleanExtra.patientData;
      event.extra = cleanExtra;
    }
    
    return event;
  },
  
  // Ignore expected/benign errors
  ignoreErrors: [
    // Recording aborted by user
    "The operation was aborted",
    "AbortError",
    "User aborted",
    // Network errors that are expected
    "Network request failed",
    "Failed to fetch",
    "Load failed",
    // Browser extensions
    "chrome-extension://",
    "moz-extension://",
    // ResizeObserver loop limit (benign)
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    // User navigation away
    "The user aborted a request",
  ],
  
  // Don't send errors from localhost development only
  enabled: !window.location.hostname.includes('localhost'),
});

// IMPORTANT: Apply saved mood theme BEFORE React renders to prevent flash of default theme
// This ensures the landing page shows the user's last selected mood color immediately
(function applyEarlyMoodTheme() {
  const savedMood = localStorage.getItem('mediscribe_mood');
  if (savedMood) {
    // Map mood IDs to their CSS classes
    const moodClasses = {
      'coral': 'mood-coral',
      'calm': 'mood-calm',
      'steady': 'mood-steady',
      'locked': 'mood-locked',
      'tired': 'mood-tired',
      'overwhelmed': 'mood-overwhelmed',
      'focused': 'mood-focused',
      'satisfied': 'mood-satisfied',
      'anxious': 'mood-anxious',
      'motivated': 'mood-motivated',
      'clinical': 'mood-clinical',
      'olive': 'mood-olive',
      'neutral': 'mood-neutral',
      'dainty': 'mood-dainty',
      'turquoise': 'mood-turquoise',
      'eggplant': 'mood-eggplant',
      'sunset': 'mood-sunset'
    };
    const moodClass = moodClasses[savedMood];
    if (moodClass) {
      document.documentElement.classList.add(moodClass);
    }
  }
  // Feb 27, 2026 — high-contrast accessibility mode. Forces all primary
  // accents to slate/emerald regardless of mood theme. Apply BEFORE React
  // renders to prevent a flash of the unmodified theme on cold start.
  try {
    if (localStorage.getItem('writeright_high_contrast') === 'true') {
      document.documentElement.classList.add('high-contrast');
      document.body && document.body.classList.add('high-contrast');
    }
  } catch { /* ignore */ }
})();

// Global error handler - catches errors that escape React
window.onerror = function(message, source, lineno, colno, error) {
  console.error('Global error:', message, source, lineno, colno, error);
  
  // Report to Sentry
  if (error) {
    Sentry.captureException(error);
  } else {
    Sentry.captureMessage(`Global error: ${message}`, 'error');
  }
  
  // Show emergency reset UI
  const root = document.getElementById('root');
  if (root && !document.getElementById('emergency-reset')) {
    const resetDiv = document.createElement('div');
    resetDiv.id = 'emergency-reset';
    resetDiv.innerHTML = `
      <div style="position:fixed;inset:0;background:#0f172a;display:flex;align-items:center;justify-content:center;z-index:99999">
        <div style="background:#1e293b;padding:24px;border-radius:12px;max-width:400px;text-align:center">
          <h1 style="color:#f87171;font-size:20px;font-weight:bold;margin-bottom:8px">App Error</h1>
          <p style="color:#cbd5e1;margin-bottom:16px">Something went wrong. Click below to clear your browser data and reload.</p>
          <button onclick="sessionStorage.clear();window.location.href=window.location.origin" style="background:#3b82f6;color:white;padding:10px 20px;border:none;border-radius:8px;cursor:pointer;font-weight:bold">
            Reload App
          </button>
          <p data-error-msg style="color:#64748b;font-size:12px;margin-top:16px"></p>
        </div>
      </div>
    `;
    const errorMsgEl = resetDiv.querySelector('[data-error-msg]');
    if (errorMsgEl) errorMsgEl.textContent = `Error: ${message}`;
    document.body.appendChild(resetDiv);
  }
};

// Unhandled promise rejection handler
window.onunhandledrejection = function(event) {
  console.error('Unhandled rejection:', event.reason);
  // Report to Sentry
  Sentry.captureException(event.reason);
};

// Sentry Error Boundary Fallback Component
const SentryFallback = ({ error, resetError }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999
  }}>
    <div style={{
      background: '#1e293b',
      padding: '24px',
      borderRadius: '12px',
      maxWidth: '400px',
      textAlign: 'center'
    }}>
      <h1 style={{ color: '#f87171', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
        Something went wrong
      </h1>
      <p style={{ color: '#cbd5e1', marginBottom: '16px' }}>
        We've been notified and are working on a fix.
      </p>
      <button 
        onClick={() => {
          resetError();
          window.location.reload();
        }}
        style={{
          background: '#3b82f6',
          color: 'white',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 'bold',
          marginRight: '8px'
        }}
      >
        Try Again
      </button>
      <button 
        onClick={() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = window.location.origin;
        }}
        style={{
          background: '#475569',
          color: 'white',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 'bold'
        }}
      >
        Reset App
      </button>
    </div>
  </div>
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Register service worker for offline support
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (reg) => console.log('SW registered:', reg.scope),
      (err) => console.warn('SW registration failed:', err)
    );
  });
}
